import React, { useState } from 'react';
import Checkbox from '../../shared/Checkbox';
import ListTable from '../../shared/ListTable';
import { PERMISSION_CAPABILITIES, SETTINGS_ROLES, permissionChanges } from '../../../../models/SettingsModel';
import { plural } from '../../../../models/LibraryModel';
import { useSettingsState } from '../../../../viewModels/useSettingsModel';

/**
 * Settings > Permissions (Navy Phase 0, story 30): a row per role, a column
 * per capability, each cell a checkbox. Ticks stage into a draft and Update
 * permissions commits the lot, so a mis-click can be walked back with Cancel.
 * Both stay disabled until the draft differs from what's saved, so unticking a
 * box back disables them again, and sorting the roles loses nothing.
 */
function PermissionsSection({ model, onShowToast }) {
  const { permissions, permissionSort } = useSettingsState(model);
  const [draft, setDraft] = useState(permissions);
  const changes = permissionChanges(permissions, draft);

  const toggle = (capability, role, held) => setDraft((d) => ({ ...d, [capability]: { ...d[capability], [role]: held } }));

  const cancel = () => {
    setDraft(permissions);
    onShowToast?.('Changes discarded.', 'info');
  };

  const save = () => {
    const changed = model.savePermissions(draft);
    if (!changed) return;
    setDraft(model.getState().permissions);
    onShowToast?.(`Permissions updated. ${changed} ${plural(changed, 'change', 'changes')}.`, 'success');
  };

  // The role column is pinned at 176, which holds "Automation operator" on one
  // line; each capability is 132, its title centred over its checkboxes.
  const columns = [
    {
      key: 'role',
      title: 'Role',
      width: '176px',
      sort: 'text',
      value: (role) => role,
      render: (role) => <span className="library-cell library-cell--strong list-table-cut">{role}</span>,
    },
    ...PERMISSION_CAPABILITIES.map((cap) => ({
      key: cap.id,
      title: cap.label,
      width: '132px',
      align: 'center',
      render: (role) => (
        <Checkbox
          checked={!!draft[cap.id][role]}
          ariaLabel={`${role} - ${cap.label}`}
          onChange={(held) => toggle(cap.id, role, held)}
        />
      ),
    })),
  ];

  return (
    <>
      <section className="navy-settings-section" aria-labelledby="navy-permissions-title">
        <div className="navy-settings-head">
          <h2 className="navy-settings-title" id="navy-permissions-title">Role permissions</h2>
        </div>
        <div className="library-panel">
          <p className="navy-settings-desc">Set the capabilities each role carries.</p>
          <ListTable
            label="Role permissions"
            columns={columns}
            rows={SETTINGS_ROLES}
            rowKey={(role) => role}
            sort={permissionSort}
            onSortChange={(sort) => model.setPermissionSort(sort)}
          />
        </div>
      </section>

      <div className="navy-settings-actions">
        <button type="button" className="resui-btn resui-btn--secondary" disabled={!changes} onClick={cancel}>Cancel</button>
        <button type="button" className="resui-btn resui-btn--primary" disabled={!changes} onClick={save}>Update permissions</button>
      </div>
    </>
  );
}

export default PermissionsSection;
